import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  type UseModalProps,
} from "@chakra-ui/react";
import { DropZone, FormRow, Select, TextField } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import { MediaList, MediaModel } from "@src/utils/components/dropzone";
import {
  fieldToInputProps,
  fieldToSelectProps,
} from "@src/utils/forms/inputHelpers";
import { arrayLengthMin, required } from "@src/utils/forms/validators";
import { FormMode } from "@src/utils/types";
import { ContractorSelect } from "@src/widgets/ContractorSelect";
import { pull } from "lodash";
import { observer } from "mobx-react-lite";
import type React from "react";
import { type FunctionComponent, useContext, useEffect } from "react";
import { DocumentsCtx } from "../../StoreDocuments";
import { RelatedTo } from "../contract/StoreContractForm";
import { useOptions } from "./Common";
import type { IFormOtherDocumentSerialized } from "./FormOtherDocument";
import { FormOtherDocumentCtx } from "./StoreOtherDocumentForm";

export const ModalOtherDocumentForm: FunctionComponent<
  React.PropsWithChildren<
    UseModalProps & {
      title?: React.ReactNode;
      mode: FormMode;
      loading?: boolean;
      primaryActionContent: string;
      defaultContractorName?: string;
      onSubmit(arg: IFormOtherDocumentSerialized): void;
      prefillFormFields?: {
        projectId?: string[];
        brandId?: string;
        clientId?: string;
        contractorId?: string;
      };
    }
  >
> = observer(function ModalOtherDocumentForm({
  title,
  onSubmit,
  onClose,
  mode,
  defaultContractorName,
  isOpen,
  primaryActionContent,
  loading,
  prefillFormFields,
}) {
  const store = useContext(FormOtherDocumentCtx);
  const documentsStore = useContext(DocumentsCtx);

  useOptions(store, documentsStore);

  useEffect(() => {
    if (prefillFormFields?.brandId) {
      store.form.related_to.onChange(RelatedTo.Brand);
      store.form.brand_id.onChange(prefillFormFields?.brandId);
    }
    if (prefillFormFields?.projectId) {
      store.form.related_to.onChange(RelatedTo.Projects);
      store.form.project_ids.onChange(prefillFormFields?.projectId);
    }
    if (prefillFormFields?.clientId) {
      store.form.related_to.onChange(RelatedTo.Client);
      store.form.client_id.onChange(prefillFormFields?.clientId);
    }
    if (prefillFormFields?.contractorId) {
      store.form.related_to.onChange(RelatedTo.Contractor);
      store.form.contractor_id.onChange(prefillFormFields?.contractorId);
    }
  }, [prefillFormFields]);

  useEffect(() => {
    store.form.related_to.$ === RelatedTo.Client
      ? store.form.client_id.validators(required)
      : store.form.client_id.validators();

    store.form.related_to.$ === RelatedTo.Brand
      ? store.form.brand_id.validators(required)
      : store.form.brand_id.validators();

    store.form.related_to.$ === RelatedTo.Contractor
      ? store.form.contractor_id.validators(required)
      : store.form.contractor_id.validators();

    store.form.related_to.$ === RelatedTo.Projects
      ? store.form.project_ids.validators(arrayLengthMin(1))
      : store.form.project_ids.validators();
  }, [store.form.related_to.$]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" trapFocus={false}>
      <ModalOverlay />

      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormRow title={<Trans>Title</Trans>}>
            <TextField
              id="field-other-document-title"
              {...fieldToInputProps(store.form.title)}
              type="text"
            />
          </FormRow>
          <FormRow title={<Trans>Related to</Trans>}>
            <Select
              asPortal
              id="field-other-document-related"
              label={""}
              {...fieldToInputProps(store.form.related_to)}
              options={store.relatedToOptions}
              placeholder={t`Level of relation`}
              onChange={(val) => {
                if (val !== RelatedTo.Projects) {
                  store.form.project_ids.reset();
                }

                if (val !== RelatedTo.Client) {
                  store.form.client_id.reset();
                }

                if (val !== RelatedTo.Brand) {
                  store.form.brand_id.reset();
                }

                if (val !== RelatedTo.Contractor) {
                  store.form.contractor_id.reset();
                }
                store.form.related_to.onChange(val);
              }}
            />
          </FormRow>
          {store.form.form.$.related_to.$ === RelatedTo.Projects && (
            <FormRow title={<Trans>Projects</Trans>}>
              <div>
                <Select
                  asPortal
                  isMulti
                  placeholder={t`Select project/s`}
                  {...fieldToSelectProps(
                    store.form.project_ids,
                    store.projectOptions,
                  )}
                />
              </div>
            </FormRow>
          )}
          {store.form.form.$.related_to.$ === RelatedTo.Client && (
            <FormRow title={<Trans>Client</Trans>}>
              <Select
                asPortal
                id="field-other-document-client"
                label={""}
                {...fieldToInputProps(store.form.client_id)}
                isDisabled={store.form.form.$.related_to.$ !== RelatedTo.Client}
                options={store.clientOptions}
                placeholder={t`Client`}
              />
            </FormRow>
          )}
          {store.form.form.$.related_to.$ === RelatedTo.Brand && (
            <FormRow title={<Trans>Brand</Trans>}>
              <Select
                asPortal
                id="field-other-document-brand"
                label={""}
                isDisabled={store.form.form.$.related_to.$ !== RelatedTo.Brand}
                {...fieldToInputProps(store.form.brand_id)}
                options={store.brandOptions}
                placeholder={t`Brand`}
              />
            </FormRow>
          )}
          {store.form.form.$.related_to.$ === RelatedTo.Contractor && (
            <FormRow title={<Trans>Contractor</Trans>}>
              <ContractorSelect
                field={store.form.contractor_id}
                defaultName={defaultContractorName}
                waitForDefaultName={mode === FormMode.EDIT}
              />
            </FormRow>
          )}
          <DropZone
            accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/docx, application/pdf, text/plain, application/msword, image/jpeg, image/png"
            allowMultiple={true}
            onDrop={(files) => {
              store.form.files.onChange([
                ...store.form.files.$,
                ...files.map((f) => new MediaModel(f)),
              ]);
            }}
          />
          <MediaList
            files={store.form.files.$}
            onDelete={(val) => {
              pull(store.form.files.$, val);
            }}
          />
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="grey" onClick={onClose} variant="outline">
            <Trans>Cancel</Trans>
          </Button>
          <Button
            ml={3}
            isLoading={loading}
            onClick={async () => {
              const data = await store.form.validate();
              if (data === null) return console.log(store.form.form.error);

              const currentlySelected = store.form.related_to.$;

              if (currentlySelected === RelatedTo.Client) {
                delete data.brand_id;
              } else if (currentlySelected === RelatedTo.Brand) {
                delete data.client_id;
              } else if (currentlySelected === RelatedTo.Projects) {
                delete data.brand_id;
                delete data.client_id;
              }

              onSubmit(data);
            }}
          >
            {primaryActionContent}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});
